/**
 * Confirming a case — which now BOOKS it.
 *
 * The bookable times are everything the board already knows was offered to this
 * candidate, from BOTH places it can be recorded:
 *
 *  - a blocked slot stamped with their offer (the holds overview), and
 *  - the times on their latest "offered" tracking entry.
 *
 * The second source is the fix for a real dead end: a recruiter may propose a
 * FREE slot without blocking it — `withOffer` says so explicitly and ignores
 * unheld starts — so nothing was stamped, the holds list was empty, and this
 * modal answered "there is nothing to book" about a time the card was
 * displaying back to the recruiter one line above (Theresa, Sep 2026). The
 * proposed time is now persisted with its ISO start on the tracking entry
 * (`slotMeta.start`), which is what makes it bookable here.
 *
 * Three states, and the difference matters because only two of them create
 * anything:
 *  - exactly one offered time  → confirm books it, and says so plainly;
 *  - several                   → the recruiter picks which one was accepted;
 *  - none                      → nothing to book. The status still moves to
 *                                confirmed, but no invitation goes anywhere,
 *                                and the body says that rather than implying
 *                                a booking that will not happen.
 */
import { useState } from 'react';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Input } from '@finn-auto/design-system/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import type {
  PanelConfig,
  SchedulingCandidate,
  SlotHold,
  TrackingDoc,
} from '../../types/taActionBoard';
import { bookableOffers } from './bookableOffers';
import type { CaseConfirmOpts } from './actionModalTypes';
import { ModalActions } from './ModalActions';
import { fmtSlotRange, fullNameFromEmail } from './format';

export function ConfirmCaseBody({
  candidate,
  panel,
  holds,
  tracking,
  onConfirm,
  onCancel,
}: {
  candidate: SchedulingCandidate;
  panel: PanelConfig | null;
  /** This role's holds — the ones stamped with this candidate's offer are bookable */
  holds: SlotHold[];
  /** Their tracking doc — carries times proposed WITHOUT a hold behind them */
  tracking: TrackingDoc | undefined;
  onConfirm: (start: string | null, opts: CaseConfirmOpts) => void;
  onCancel: () => void;
}) {
  const offered = bookableOffers(holds, tracking, candidate.opportunityId, panel?.minutes ?? 60);
  const [picked, setPicked] = useState(offered[0]?.start ?? '');
  // REMOTE by default (Theresa, Sep 2026) — most cases run on a Meet link, and an onsite
  // default made every remote booking a two-step correction that also demanded a phone number
  // it did not need. Onsite stays one dropdown away, and still books a room by default.
  const [mode, setMode] = useState<'onsite' | 'remote'>('remote');
  const [bookRoom, setBookRoom] = useState(true);
  const [contactPhone, setContactPhone] = useState('');
  const slot = offered.find((h) => h.start === picked) ?? offered[0] ?? null;
  const noForm = !panel?.feedbackTemplate;
  // Onsite candidates need a number to call on arrival — same guardrail as the
  // final round; without it the confirmation email ships a placeholder.
  const needsPhone = offered.length > 0 && mode === 'onsite' && contactPhone.trim().length === 0;

  return (
    <>
      <div className="space-y-3 text-sm text-gray-700">
        {offered.length === 0 ? (
          <p>
            <span className="font-semibold">{candidate.name}</span> has no proposed time on record,
            so there is nothing to book. The status moves to “confirmed” and{' '}
            <span className="font-semibold">no invitation is sent</span> — book it by hand, or
            propose a time first and confirm again.
          </p>
        ) : (
          <>
            <p>
              Booking the case for <span className="font-semibold">{candidate.name}</span>.
            </p>
            {offered.length > 1 ? (
              <label className="block">
                <span className="text-xs font-semibold text-gray-800">Which slot did they take?</span>
                <Select value={picked} onValueChange={setPicked}>
                  <SelectTrigger className="mt-1 w-full text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {offered.map((h) => (
                      <SelectItem key={h.start} value={h.start}>
                        {fmtSlotRange(h)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            ) : (
              <p className="rounded-md bg-gray-50 px-3 py-2 font-semibold text-gray-900">
                {slot ? fmtSlotRange(slot) : ''}
              </p>
            )}

            {slot && (
              <div className="space-y-3">
                <Select value={mode} onValueChange={(v) => setMode(v as 'onsite' | 'remote')}>
                  <SelectTrigger label="Onsite or remote" className="w-full text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Remote first: it is the default, and the list should read in that order. */}
                    <SelectItem value="remote">Remote — Google Meet link</SelectItem>
                    <SelectItem value="onsite">Onsite — in the office</SelectItem>
                  </SelectContent>
                </Select>
                {mode === 'onsite' && (
                  <div className="space-y-3 rounded-xl border border-gray-200 p-3">
                    <Input
                      label="Contact phone for the candidate (they call this on arrival)"
                      value={contactPhone}
                      onChange={(e) => setContactPhone((e.target as HTMLInputElement).value)}
                    />
                    <Checkbox
                      label="Book a meeting room (a free one is reserved automatically)"
                      labelClassName="text-sm text-gray-700"
                      checked={bookRoom}
                      onCheckedChange={(v) => setBookRoom(v === true)}
                    />
                  </div>
                )}
              </div>
            )}

            <ul className="list-disc space-y-1 pl-4 text-xs text-gray-600">
              <li>
                Calendar invite to{' '}
                {(panel?.interviewers || []).map(fullNameFromEmail).join(' + ') || 'the panel'}
                {mode === 'onsite' ? ', at the FINN office' : ', with a Google Meet link'}
              </li>
              <li>Lever interview with the panel’s feedback form attached</li>
              <li>
                Confirmation email to {candidate.name}, with a calendar file they can add
                themselves and a link to reschedule or cancel
              </li>
              <li>Their remaining holds are released</li>
            </ul>

            {noForm && (
              <p className="rounded-md bg-[#FCEEE2] px-3 py-2 text-xs text-[#854F0B]">
                No feedback form is set for this panel, so the booking will be refused. Set one in
                “Edit panel” first — a Lever interview without a scorecard is a round nobody can
                evaluate.
              </p>
            )}

            <p className="text-xs text-gray-500">
              You have 10 seconds to stop this before anything is created.
            </p>
          </>
        )}
      </div>
      <ModalActions
        confirmLabel={offered.length === 0 ? 'Mark confirmed' : 'Confirm and book'}
        confirmDisabled={offered.length > 0 && (noForm || needsPhone)}
        onConfirm={() =>
          onConfirm(offered.length === 0 ? null : (slot?.start ?? null), {
            mode,
            bookRoom: mode === 'onsite' ? bookRoom : false,
            contactPhone: mode === 'onsite' ? contactPhone.trim() : null,
          })
        }
        onCancel={onCancel}
      />
    </>
  );
}
